/**
 * Advanced usage only
 *
 * API Status: unstable
 *
 * @packageDocumentation
 * @module components/BaseButton
 */
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
// Import last
import cx from 'classnames';
import { ButtonVariant, getButtonDesignTokens } from '..';
import { BrowserName, getBrowserName } from '../../../browser-name';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { markEvent } from '../../../marked-events';
import { useDesignTokens } from '../../../theme';
import { getOSName, OSName } from '../../../util';
import { setRef } from '../../../util/ref-util';
import { useDensity } from '../../Density';
import { useDirectSpeech } from '../../DirectSpeech/DirectSpeechProvider';
import { Icon } from '../../Icon';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';
import bundle from '../NovaComponents-Button-gui-icu.properties';
import './BaseButton.css';
import cn from './BaseButton.css.json';

// CPP stand for continuous pointer press
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CPP_START_TIME = 400;
const CPP_INTERVAL = 75;
const useTextLocalizer = createUseTextLocalizer(bundle);

/**
 * @internal
 */
export const BaseButton = /*#__PURE__*/forwardRef(function BaseButton({
  enableContinuousPress = false,
  children,
  className,
  disabled = false,
  focusable,
  icon,
  onBlur,
  onClick,
  onPress,
  onMouseDown,
  onMouseUp,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onKeyDown,
  onKeyUp,
  style: userStyle,
  testId,
  text,
  theme,
  tokens,
  type,
  variant,
  ariaLabel: originalAriaLabel,
  tooltip: originalTooltip,
  ariaLabelledBy,
  ariaDescribedBy,
  ...props
}, ref) {
  const [isActive, setIsActive] = useState(false);
  const v = variant || ButtonVariant.Standard;
  const thisButtonRef = useRef(null);
  const localize = useTextLocalizer();
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const compTokens = {
    ...getButtonDesignTokens(dt, {
      disabled,
      variant: v,
      tokens,
      density
    }),
    ...theme?.(dt, {
      disabled
    })
  };
  const style = {
    ...compTokens,
    ...userStyle
  };
  let clsName;
  if (v === ButtonVariant.Small || v === ButtonVariant.PrimarySmall || v === ButtonVariant.LightSmall || v === ButtonVariant.SmallIcon) {
    clsName = cn.small;
  } else if (v === ButtonVariant.RoundIcon) {
    clsName = cn.roundIcon;
  } else {
    clsName = cn.button;
  }
  const isNormalButton = v === ButtonVariant.Standard || v === ButtonVariant.Small || v === ButtonVariant.Primary || v === ButtonVariant.PrimarySmall;
  const isNormalButtonWithText = isNormalButton && text;
  const getIconA11yInfo = useIconA11yInfo();
  const {
    ...iconA11yInfo
  } = icon ? getIconA11yInfo(icon) : {};
  let tooltip, ariaLabel;
  if (icon && !text) {
    // Icon only
    tooltip = originalTooltip || (originalTooltip === '' ? undefined : iconA11yInfo.tooltip);
    ariaLabel = originalAriaLabel || originalTooltip || (originalTooltip === '' && originalAriaLabel === '' ? undefined : iconA11yInfo.ariaLabel);
  } else {
    // Text or Icon + text
    tooltip = originalTooltip ?? '';
    ariaLabel = originalAriaLabel ?? (originalTooltip === undefined ? '' : `${text}, ${originalTooltip}`);
  }
  const textRef = useRef(null);
  const setTruncationRef = useTruncationTooltip({
    tooltip,
    truncateRef: textRef
  });
  const pressEvtTriggerRef = useRef({
    timeoutId: -1,
    disabled,
    onPress
  });
  pressEvtTriggerRef.current.disabled = disabled;
  pressEvtTriggerRef.current.onPress = onPress;
  const firePressEventContinuously = delay => {
    if (enableContinuousPress && !pressEvtTriggerRef.current.disabled) {
      pressEvtTriggerRef.current.onPress?.(pressEvtTriggerRef.current.event);
      pressEvtTriggerRef.current.timeoutId = window.setTimeout(() => {
        firePressEventContinuously(CPP_INTERVAL);
      }, delay);
    }
  };
  const cancelPressEvent = () => {
    if (pressEvtTriggerRef.current.timeoutId >= 0) {
      clearTimeout(pressEvtTriggerRef.current.timeoutId);
      pressEvtTriggerRef.current.timeoutId = -1;
    }
  };
  const setButtonRef = useCallback(button => {
    thisButtonRef.current = button;
    setRef(ref, button);
  }, [ref]);
  useEffect(() => {
    return () => cancelPressEvent();
  }, []);
  const directSpeech = useDirectSpeech();

  // announce immediately when a button is changed from enabled to disabled without losing its focus.
  useEffect(() => {
    if (document.activeElement === thisButtonRef.current) {
      const label = ariaLabel || tooltip || text;
      const sTooltip = tooltip && !ariaLabel?.startsWith(tooltip) ? tooltip : '';
      const message = disabled ? localize('disabledButton.msg', {
        label: label,
        tooltip: sTooltip
      }) : localize('enabledButton.msg', {
        label: label,
        tooltip: sTooltip
      });
      setTimeout(() => {
        directSpeech?.notify(message);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);
  return /*#__PURE__*/_jsxs("button", {
    ...props,
    "aria-disabled": disabled,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    className: cx(className, clsName, !disabled && isActive && cn.active, disabled && cn.disabled, dt.themeVariant === 'high-contrast' ? cn.highContrast : undefined, isNormalButton && cn.normalButton, isNormalButtonWithText && cn.normalButtonWithText),
    "data-testid": testId,
    ref: e => {
      setButtonRef(e);
      setTruncationRef(e);
    },
    style: style,
    tabIndex: focusable ?? true ? undefined : -1,
    title: tooltip,
    type: type || 'button',
    onBlur: e => {
      onBlur?.(e);
      setIsActive(false);
    },
    onClick: e => {
      // In firefox broswer, type Spacebar may trigger onClick event,
      // so below condition added to avoid it.
      // NOTE: e.nativeEvent.detail === 0 on FF when pressing Spacebar
      if (getBrowserName() !== BrowserName.Firefox || e.nativeEvent.detail !== 0) {
        onClick?.(e);
        if (!disabled && !enableContinuousPress) {
          type !== 'submit' && e.preventDefault();
          onPress?.(e);
        }
      }
    },
    onKeyDown: e => {
      const isActivationKey = e.key === 'Enter' || e.key === ' ';
      const hasModifier = e.altKey || e.ctrlKey || e.shiftKey || e.metaKey;
      const activationIsAllowed = isActivationKey && !hasModifier;
      if (isActivationKey && type !== 'submit') {
        e.preventDefault();
      }
      if (activationIsAllowed) {
        if (disabled) {
          e.stopPropagation();
          return;
        }
        onKeyDown?.(e);
        markEvent(e);
        setIsActive(true);
        if (enableContinuousPress) {
          onPress?.(e);
        }
      } else {
        onKeyDown?.(e);
      }
    },
    onKeyUp: e => {
      const isActivationKey = e.key === 'Enter' || e.key === ' ';
      const hasModifier = e.altKey || e.ctrlKey || e.shiftKey || e.metaKey;
      const activationIsAllowed = isActivationKey && !hasModifier;
      if (isActivationKey) {
        e.preventDefault();
      }
      if (activationIsAllowed) {
        if (disabled) {
          e.stopPropagation();
          return;
        }
        if (isActive) {
          setIsActive(false);
          if (!enableContinuousPress) {
            onPress?.(e);
          }
        }
      }
      onKeyUp?.(e);
    },
    onMouseDown: e => {
      onMouseDown?.(e);
      // NOTE: e.nativeEvent.detail === 0 on FF when pressing Spacebar
      if (!disabled && enableContinuousPress && (getBrowserName() !== BrowserName.Firefox || e.nativeEvent.detail !== 0) && e.button === 0 // main button is pressed.
      ) {
        pressEvtTriggerRef.current.event = e;
        // Do not call e.preventDefault() to ensure focus is set to the current target
        firePressEventContinuously(CPP_START_TIME);
      }

      // FireFox and Safari doesn't set the focus to the clicked button tag but to the nearest parent DOM which is focusable
      // This behavior has to be stopped by calling prevent default when the original event is 'mousedown'
      // and set the focus explicitly to the button.
      // refer to https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
      if (getOSName() === OSName.Mac) {
        const browserName = getBrowserName();
        if (browserName === BrowserName.Firefox || browserName === BrowserName.Safari) {
          thisButtonRef.current?.focus();
          e.preventDefault();
        }
      }
    },
    onMouseUp: e => {
      onMouseUp?.(e);
      e.preventDefault();
      cancelPressEvent();
    },
    onMouseLeave: e => {
      onMouseLeave?.(e);
      e.preventDefault();
      cancelPressEvent();
    },
    children: [icon && /*#__PURE__*/_jsx(Icon, {
      ...icon,
      className: cx(text && cn.iconWithText, icon.className, cn.iconInner)
      // Icon tooltip/aria-label should only go on the base of the button
      ,
      tooltip: "",
      ariaLabel: "",
      ariaHidden: true,
      disabled: false
    }), /*#__PURE__*/_jsx("span", {
      className: cn.text,
      ref: textRef,
      children: text
    }), children]
  });
});
//# sourceMappingURL=BaseButton.js.map